import styled from 'styled-components';
import { useCampaignURLHandler } from 'components/PageWrappers/Hooks/Campaigns/UseCampaignURLHandler';

import { AdSummary } from 'types';

import { CadCard } from 'components/PageWrappers/SharedComponents/CadCard/CadCard';
import { Link } from 'components/Toolkit/Button/Link';
import { media } from 'helpers/breakpoints';
import type {
  CardOrientation,
  CardVariations,
} from 'components/Toolkit/Card/Card';

import convertToFriendlyPath from 'utils/FriendlyPath';
import { accessibleOnClick } from 'utils/Accessibility';

interface RelatedAdsGridProps {
  adId?: number;
  relatedAds: AdSummary[];
  cdnUrl: string;
  domain: string;
  forUseWithModal?: boolean;
  variant?: CardVariations;
  orientation?: CardOrientation;
  testId?: string;
  updateCampaignNumber?: number;
}

const List = styled.ul<{ variant: CardVariations; forUseWithModal?: boolean }>`
  display: grid;
  grid-template-columns: ${({ variant }) =>
    variant === 'ENHANCED' ? '1fr' : '1fr 1fr'};
  grid-gap: ${({ theme }) => theme.spacing.S8};

  ${media.medium} {
    grid-template-columns: ${({ variant, forUseWithModal }) =>
      variant === 'ENHANCED'
        ? '1fr'
        : forUseWithModal
        ? '1fr 1fr'
        : '1fr 1fr 1fr'};
    grid-gap: ${({ theme, forUseWithModal }) =>
      !forUseWithModal ? theme.spacing.M16 : theme.spacing.S8};
  }
`;

const SLink = styled(Link)`
  height: 100%;
`;

function RelatedAdsGrid({
  adId,
  relatedAds,
  cdnUrl,
  domain,
  forUseWithModal,
  variant = 'SIMPLE',
  orientation = 'VERTICAL',
  updateCampaignNumber,
  testId,
}: RelatedAdsGridProps) {
  const { handleCampaignAdRouterPush } = useCampaignURLHandler();

  return (
    <List
      variant={variant}
      forUseWithModal={forUseWithModal}
      data-testid={testId}
    >
      {relatedAds.map((ad, index) => {
        const friendlyPath = convertToFriendlyPath(
          domain,
          ad.friendlyUrl!,
          updateCampaignNumber!,
        );

        return (
          <li key={index}>
            <SLink
              isFakeLink
              {...accessibleOnClick(
                () => handleCampaignAdRouterPush(friendlyPath),
                'link',
              )}
              ofType="SECONDARY"
            >
              <CadCard
                adId={adId}
                variant={variant}
                orientation={orientation}
                cdnUrl={cdnUrl}
                {...ad}
              />
            </SLink>
          </li>
        );
      })}
    </List>
  );
}

export { RelatedAdsGrid };
